import { useAuthState } from "@/store/auth";
import { PaymentMethodContainer } from "../../invoices/payment-method";
import { useInvoiceState } from "@/store/invoices";
import { PaymentMethodScreenModal } from "@/types/invoice";
import {
  CreditPackageDetailSortByEnum,
  CreditTypeEnum,
  SortEnum,
  WorkerDepartmentEnum,
} from "@/__generated__/graphql";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { useRoute } from "@/components/shared/router";
import { getErrorMessage } from "@/lib/helpers/string";
import { showToast } from "@/components/ui/toast/show-toast";
import { useProfileState } from "@/store/profile";
import { getPlatform } from "@/lib/utils";
import { CreditAccountTopUpScreen } from "./CreditAccountTopUp.screen";
import { useCreditPackageDetailsByFiltersQuery } from "@/gqlhooks/profile/useCreditPackageDetailsByFiltersQuery";
import { useTopUpCreditAccountMutation } from "@/gqlhooks/profile/useTopupCreditAccountMutation";
import { CreditPackageData } from "@/types/profile";
import { getDepartmentLinkedName } from "../utils";

export type CreditAccountTopUp = {
  openModal: boolean;
  handleClose(): void;
  creditPackages: CreditPackageData[];
  handleSelectPackage: (packageId: string) => Promise<void>;
  packageDetailsLoading: boolean;
  topUpLoading: boolean;
};

export const CreditAccountTopUpContainer = () => {
  const { isMobile } = useWindowDimensions();
  const isWeb = getPlatform() === "web";
  const { push } = useRoute();
  const {
    setCreditTopUpModal,
    data: { creditTopUpModalOpen, creditAccountType },
  } = useProfileState();
  const selectedDepartmentName = getDepartmentLinkedName(creditAccountType);

  const {
    list: creditPackagesList,
    loading: creditPackageDetailsByFilterLoading,
  } = useCreditPackageDetailsByFiltersQuery({
    status: ["active"],
    sortBy: [CreditPackageDetailSortByEnum.Amount],
    sort: [SortEnum.Desc],
    department: selectedDepartmentName
      ? (selectedDepartmentName as WorkerDepartmentEnum)
      : undefined,
  });

  const {
    setInvoiceId,
    showPaymentMethodModal,
    setTopUpCreditPackageId,
    setInvoiceDepartments,
  } = useInvoiceState();
  const { topUpCreditAccount, loading: topUpCreditAccountLoading } =
    useTopUpCreditAccountMutation();
  const {
    data: {
      userInfo: { creditAccounts },
    },
  } = useAuthState();

  const selectedCreditAccount = creditAccounts.find(
    (credit) =>
      credit.creditType === CreditTypeEnum.Department &&
      credit.linkedObject?.linkedType === "Department" &&
      credit.linkedObject.name === selectedDepartmentName,
  );

  const handleSelectPackage = async (packageId: string) => {
    const creditAccountId = selectedCreditAccount?.id;
    if (!creditAccountId) {
      showToast({
        title: "Credit account not found for selected type",
        type: "error",
      });
      return;
    }

    try {
      // Hit mutation top up credit account
      const result = await topUpCreditAccount({
        creditAccountId,
        topUpDetailId: packageId,
      });
      // Get the invoice result
      const invoice = result.data?.topUpCreditAccount?.invoice;
      if (invoice) {
        setInvoiceId(invoice.id);
      }
      setInvoiceDepartments([]);
      setTopUpCreditPackageId(packageId);
      setCreditTopUpModal(false);
      showPaymentMethodModal(PaymentMethodScreenModal.SELECT_PAYMENT);
      if (isMobile) {
        push({ pageKey: "selectPayment" });
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Error get top up credit account");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  };

  const handleCloseTopUp = () => {
    setCreditTopUpModal(false);
  };

  return (
    <>
      <CreditAccountTopUpScreen
        openModal={creditTopUpModalOpen}
        handleClose={handleCloseTopUp}
        creditPackages={creditPackagesList}
        handleSelectPackage={handleSelectPackage}
        packageDetailsLoading={creditPackageDetailsByFilterLoading}
        topUpLoading={topUpCreditAccountLoading}
      />
      {isWeb && <PaymentMethodContainer />}
    </>
  );
};
